import React, {KeyboardEvent, useEffect, useId, useMemo, useRef, useState} from 'react';
import classNames from "classnames";

import {ExpandMoreIcon} from "../../icons/ExpandMoreIcon";


import './PaddleLikeSelect.css';


export interface OptionProps<T> {
    value: T;
    label: string | React.ReactNode;
}

export interface SelectProps<T extends string> {
    options: OptionProps<T>[];
    value: string;
    onChange: (newValue: T) => void;
    disabled?: boolean;
}


export const PaddleLikeSelect = <T extends string>(
    {
        options,
        value,
        onChange,
        disabled,
    }: SelectProps<T>
) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listboxRef = useRef<HTMLUListElement>(null);
    const listboxId = useId();
    const triggerId = useId();
    const [isOpen, setIsOpen] = useState(false);

    const selectedIndex = useMemo(
        () => options.findIndex(option => option.value === value),
        [options, value]
    );
    const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (!target || !rootRef.current?.contains(target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (selectedIndex >= 0) {
            setActiveIndex(selectedIndex);
        }
    }, [selectedIndex]);

    useEffect(() => {
        if (isOpen) {
            listboxRef.current?.focus();
        }
    }, [isOpen]);

    const open = () => {
        if (disabled) {
            return;
        }
        setIsOpen(true);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    };

    const close = () => {
        setIsOpen(false);
    };

    const selectByIndex = (index: number) => {
        const option = options[index];
        if (!option) {
            return;
        }

        onChange(option.value);
        setActiveIndex(index);
        close();
        triggerRef.current?.focus();
    };

    const moveActive = (nextIndex: number) => {
        const optionsLength = options.length;
        if (optionsLength === 0) {
            return;
        }

        const normalized = (nextIndex + optionsLength) % optionsLength;
        setActiveIndex(normalized);
    };

    const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!isOpen) {
                    open();
                } else {
                    moveActive(activeIndex + 1);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (!isOpen) {
                    open();
                } else {
                    moveActive(activeIndex - 1);
                }
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (isOpen) {
                    selectByIndex(activeIndex);
                } else {
                    open();
                }
                break;
            case 'Escape':
                if (isOpen) {
                    event.preventDefault();
                    close();
                }
                break;
            default:
                break;
        }
    };

    const onListboxKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                moveActive(activeIndex + 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                moveActive(activeIndex - 1);
                break;
            case 'Home':
                event.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                event.preventDefault();
                if (options.length > 0) {
                    setActiveIndex(options.length - 1);
                }
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                selectByIndex(activeIndex);
                break;
            case 'Escape':
                event.preventDefault();
                close();
                triggerRef.current?.focus();
                break;
            case 'Tab':
                close();
                break;
            default:
                break;
        }
    };

    const selectedOption = options[selectedIndex] || null;
    const activeOptionId = options[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined;

    return (
        <div
            className={classNames({
                "PaddleLikeSelect": true,
                "PaddleLikeSelect--open": isOpen,
                "PaddleLikeSelect--disabled": !!disabled,
            })}
            ref={rootRef}
        >
            <button
                ref={triggerRef}
                className="PaddleLikeSelect__trigger body-large"
                type="button"
                onClick={() => (isOpen ? close() : open())}
                onKeyDown={onTriggerKeyDown}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                id={triggerId}
            >
                <span className="PaddleLikeSelect__value">
                    {selectedOption?.label}
                </span>
                <ExpandMoreIcon size={16} className="PaddleLikeSelect__arrow"/>
            </button>
            {isOpen ? (
                <ul
                    className="PaddleLikeSelect__listbox"
                    role="listbox"
                    id={listboxId}
                    aria-labelledby={triggerId}
                    aria-activedescendant={activeOptionId}
                    tabIndex={-1}
                    onKeyDown={onListboxKeyDown}
                    ref={listboxRef}
                >
                    {options.map((option, index) => {
                        const isSelected = option.value === value;
                        const isActive = index === activeIndex;

                        return (
                            <li key={option.value} role="presentation">
                                <button
                                    id={`${listboxId}-option-${index}`}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    className={classNames({
                                        "PaddleLikeSelect__option body-large": true,
                                        "PaddleLikeSelect__option--selected": isSelected,
                                        "PaddleLikeSelect__option--active": isActive,
                                    })}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => selectByIndex(index)}
                                >
                                    {option.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    )
}
